import { useState } from 'react';
import { Check, Copy, ExternalLink, Link2, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../WorkspaceContext';

export const getBookingLink = (location) => {
  if (!location?.slug) return { path: null, url: null };
  const path = `/book/${location.slug}`;
  const url =
    typeof window !== 'undefined' ? `${window.location.origin}${path}` : path;
  return { path, url };
};

/**
 * Share / copy / preview controls for the active location's booking link.
 * `compact` fits the workspace header; default suits Front Desk actions.
 */
const BookingLinkActions = ({ compact = false, className = '' }) => {
  const { activeLocation } = useWorkspace();
  const [copied, setCopied] = useState(false);
  const [shareHint, setShareHint] = useState('');

  const { path, url } = getBookingLink(activeLocation);
  const bookingOn = activeLocation?.bookingEnabled !== false;

  if (!path || !url) {
    return (
      <Link
        to="/app/manage/booking-link"
        className={`inline-flex items-center gap-2 text-sm transition-colors ${
          compact
            ? 'px-2.5 py-1.5 text-sand-100 hover:bg-sand-50/10'
            : 'px-4 py-3 font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white'
        } ${className}`}
      >
        <Link2 className="h-4 w-4" />
        {compact ? 'Set booking link' : 'Set up booking link'}
      </Link>
    );
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setShareHint('');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setShareHint('Could not copy');
    }
  };

  const shareLink = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: activeLocation?.name || 'Book with us',
          text: `Book online at ${activeLocation?.name || 'our salon'}`,
          url,
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    await copyLink();
    setShareHint('Link copied');
    setTimeout(() => setShareHint(''), 2000);
  };

  if (compact) {
    return (
      <div className={`hidden items-center gap-1 lg:flex ${className}`}>
        <div className="flex max-w-[14rem] items-center gap-2 bg-sand-50/10 px-2.5 py-1.5 text-sand-100 xl:max-w-[18rem]">
          <Link2 className="h-3.5 w-3.5 flex-none text-saffron-400" />
          <p className="min-w-0 truncate text-xs" title={url}>
            /book/{activeLocation.slug}
          </p>
          {!bookingOn && (
            <span className="flex-none text-[10px] uppercase tracking-[0.12em] text-red-300">
              Off
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={copyLink}
          title={copied ? 'Copied' : 'Copy booking link'}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-sand-100 transition-colors hover:bg-sand-50/10"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-saffron-400" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <a
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          title="Preview booking page"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-sand-100 transition-colors hover:bg-sand-50/10"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Preview
        </a>
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <div className="flex min-w-0 max-w-full items-center gap-2 bg-sand-50 px-3 py-2.5 ring-1 ring-ink-900/10 sm:max-w-xs">
        <Link2 className="h-4 w-4 flex-none text-saffron-600" />
        <p className="min-w-0 truncate text-sm text-ink-800" title={url}>
          /book/{activeLocation.slug}
        </p>
        {!bookingOn && (
          <span className="flex-none text-[10px] font-semibold uppercase tracking-[0.12em] text-red-700">
            Off
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={shareLink}
        className="inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 transition-colors hover:bg-white"
      >
        <Share2 className="h-4 w-4" />
        Share
      </button>
      <button
        type="button"
        onClick={copyLink}
        className="inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 transition-colors hover:bg-white"
      >
        {copied ? <Check className="h-4 w-4 text-saffron-600" /> : <Copy className="h-4 w-4" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <a
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-ink-900 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-sand-50 transition-colors hover:bg-ink-800"
      >
        <ExternalLink className="h-4 w-4" />
        Preview
      </a>
      {shareHint ? (
        <span className="text-xs text-ink-600/70">{shareHint}</span>
      ) : null}
    </div>
  );
};

export default BookingLinkActions;
